/**
 * The lens manifests, fetched once and shared by the switcher, both sidebars
 * and the Account page.
 *
 * Read from GET /agent-atlas/api/v1/lenses the first time anything asks and
 * cached for the session. Unlike the surface manifests, a failure is not shown
 * as an error: lenses only arrange the navigation, so without them the switcher
 * hides and the sidebar is the full one (Everything), which is what every user
 * had before lenses. Nothing is guessed from a bundled copy.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { agentAtlasAPI } from '../../services/api/agentAtlasClient';
import {
  IDLE, getState, readyState, resetState, setState, subscribe,
} from './store';

let inflight = null;

export const loadLenses = () => {
  if (getState().status === 'ready') return Promise.resolve(getState());
  if (inflight) return inflight;
  setState({ ...IDLE, status: 'loading' });
  inflight = agentAtlasAPI.getLensManifests()
    .then((body) => {
      if (!Array.isArray(body?.use_cases) || body.use_cases.length === 0) {
        throw new Error('The lens manifests came back empty.');
      }
      setState(readyState(body));
      return getState();
    })
    .catch((error) => {
      setState({ ...IDLE, status: 'error', error });
      return getState();
    })
    .finally(() => { inflight = null; });
  return inflight;
};

/** Forget what was loaded. Tests use it. */
export const resetLenses = () => {
  inflight = null;
  resetState();
};

/** { status: 'idle' | 'loading' | 'ready' | 'error', body, fingerprint, error } */
export const useLenses = () => {
  const snapshot = useSyncExternalStore(subscribe, getState, getState);
  useEffect(() => {
    if (snapshot.status === 'idle') loadLenses();
  }, [snapshot.status]);
  return snapshot;
};
